import React, { useEffect, useState } from "react";
import { fetchHistoricPayrolls } from "../../services/fetchHistoricPayrolls";
import useUserStore from "../../stores/userStore";
import { toFloat } from "../../utils/formatting";
import getFirebaseAuthToken from '../../services/fireBaseAuthenticationService';
import { BACKEND_URL } from "../../utils/constants";
import TrendGraph from "./TrendGraph";
import { Loader2 } from "lucide-react";

const CHART_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#3b82f6", "#ef4444", "#8b5cf6"];

const LastPeriodCharts = () => {
    const { currentUser } = useUserStore();
    const [historicPayrolls, setHistoricPayrolls] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const runs = await fetchHistoricPayrolls(getFirebaseAuthToken, BACKEND_URL);
                setHistoricPayrolls(runs || []);
            } catch (err) {
                console.error("Failed to fetch payrolls:", err);
                setHistoricPayrolls([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
                <Loader2 size={20} className="animate-spin" /> Loading stats...
            </div>
        );
    }

    if (!historicPayrolls.length) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
                No payroll data available yet.
            </div>
        );
    }

    const metricConfigs = [
        {
            label: "Service Rate",
            xKey: "period_end",
            yKey: "service_rate",
            data: historicPayrolls.map(p => ({
                period_end: p.period_end,
                service_rate: toFloat(p.employee_data.find(e => e.user_id === currentUser.user_id)?.service_rate || 0),
            })),
        },
        {
            label: "Income / Hour Worked",
            xKey: "period_end",
            yKey: "incomePerHour",
            data: historicPayrolls.map(p => {
                const emp = p.employee_data.find(e => e.user_id === currentUser.user_id);
                const income = toFloat(emp?.income_in_period?.total_income || 0);
                const hours = toFloat(emp?.normal_hours || 0) + toFloat(emp?.overtime_hours || 0);
                return {
                    period_end: p.period_end,
                    incomePerHour: hours > 0 ? income / hours : 0,
                };
            }),
        },
        {
            label: "Scheduled Hours",
            xKey: "period_end",
            yKey: "scheduled_hours",
            data: historicPayrolls.map(p => ({
                period_end: p.period_end,
                scheduled_hours: toFloat(p.employee_data.find(e => e.user_id === currentUser.user_id)?.scheduled_hours || 0),
            })),
        },
        {
            label: "Hours Worked",
            xKey: "period_end",
            yKey: "hoursWorked",
            data: historicPayrolls.map(p => {
                const emp = p.employee_data.find(e => e.user_id === currentUser.user_id);
                return {
                    period_end: p.period_end,
                    hoursWorked: toFloat(emp?.normal_hours || 0) + toFloat(emp?.overtime_hours || 0),
                };
            }),
        },
        {
            label: "% Scheduled Worked",
            xKey: "period_end",
            yKey: "percentWorked",
            data: historicPayrolls.map(p => {
                const emp = p.employee_data.find(e => e.user_id === currentUser.user_id);
                const scheduled = toFloat(emp?.scheduled_hours || 0);
                const worked = toFloat(emp?.normal_hours || 0) + toFloat(emp?.overtime_hours || 0);
                return {
                    period_end: p.period_end,
                    percentWorked: scheduled > 0 ? (worked / scheduled) * 100 : 0,
                };
            }),
        },
        {
            label: "Bonus",
            xKey: "period_end",
            yKey: "bonus",
            data: historicPayrolls.map(p => ({
                period_end: p.period_end,
                bonus: toFloat(p.employee_data.find(e => e.user_id === currentUser.user_id)?.income_in_period?.bonus || 0),
            })),
        },
    ];

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {metricConfigs.map(({ label, xKey, yKey, data }, i) => (
                    <div key={label} className="bg-gray-50 rounded-xl border border-gray-100 p-4">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{label}</h3>
                        <TrendGraph data={data} xKey={xKey} yKey={yKey} color={CHART_COLORS[i % CHART_COLORS.length]} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default React.memo(LastPeriodCharts);
